import { useMemo, useEffect, useState, useCallback } from "react"
import { useDispatch } from "react-redux"
import { copyToClipboard } from "../Helpers/Functions/clipboard"
import { successMessage } from "../Actions/Client/MessagesActions"
import {
    APICollaborationParams,
    APICollaborationModel
} from "../Models/API/CollaboratorModel"
import { useCollaborators } from "./useCollaborators"
import { hasValue, FetchState } from "../Models/Client/FetchState"
import { MainThunkDispatch } from "../Reducers"
import { loadMoreCollaborators } from "../Actions/API/Collaborator/CollaboratorActions"
import { Result } from "../Models/Utility/Result"
import { UseAsyncCommand } from "./useAsyncCommand"

/**
 * Hook to provide function to copy all collaborator emails.
 * The processing will be asynchronous if collaborators are only partially fetched.
 */
export function useCollaboratorCopyEmails(
    appID: string,
    params: APICollaborationParams
): UseAsyncCommand {
    const dispatch = useDispatch<MainThunkDispatch>()
    const [state, setState] = useState<FetchState<undefined>>(undefined)
    const itemsFetch = useCollaborators(appID, params)
    const handleCopy = useCallback(
        (items: APICollaborationModel[]) => {
            copyToClipboard(items.map(i => i.invitedEmail).join("\n"))

            dispatch(
                successMessage(
                    items.length > 1 ? "Emails copied" : "Email copied"
                )
            )

            setState(Result.ok(undefined))
        },
        [dispatch]
    )

    useEffect(() => {
        if (state !== "loading") {
            return
        }

        if (
            !hasValue(itemsFetch) ||
            itemsFetch.value.items.length < itemsFetch.value.total
        ) {
            // use large page size for speed
            dispatch(loadMoreCollaborators(appID, 1000))
            return
        }

        handleCopy(itemsFetch.value.items)
    }, [dispatch, appID, handleCopy, itemsFetch, state])

    return useMemo<UseAsyncCommand>(() => {
        const common = {
            isProcessing: state === "loading",
            wasSuccessful: hasValue(state) && !state.isError,
            reset: () => setState(undefined)
        }

        if (!hasValue(itemsFetch)) {
            return common
        }

        // If collaboration list is already fetched to the end when copy is requested,
        // just copy them. Otherwise, start loading the rest and copy when completed.
        return {
            ...common,
            perform: async () => {
                if (itemsFetch.value.items.length === itemsFetch.value.total) {
                    handleCopy(itemsFetch.value.items)
                    return
                }

                setState("loading")
            }
        }
    }, [itemsFetch, handleCopy, state])
}
